import React, { useEffect, useState } from 'react';
import { Shield } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Spinner } from '../ui';
import { ClientsHub } from '../accounts/ClientsHub';
import { AnalyticsContent } from './AnalyticsContent';
import { UsersTabs } from './UsersTabs';
import { PipelineBoard } from '../pipeline/PipelineBoard';
import { SignupsTab } from './admin/SignupsTab';
import { ADMIN_VIEW_TABS, AdminViewTab } from '../../utils/adminView';

// Shared with the sidebar/drawer nav highlight so the two can't disagree.
const OUTER_TABS = ADMIN_VIEW_TABS;
type OuterTab = AdminViewTab;

const TABS: { key: OuterTab; label: string }[] = [
  { key: 'pipeline', label: 'Pipeline' },
  { key: 'clients', label: 'Clients' },
  { key: 'signups', label: 'Signups' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'users', label: 'Users' },
];

/** Top-level admin page: "Pipeline" (default) | "Clients" | "Signups" | "Analytics" | "Users".
 *  Tab lives in `?view=`; the single admin gate lives here — children do not re-check. */
export const AnalyticsTabs: React.FC = () => {
  const { user, isLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view') as OuterTab | null;
  // Absent or invalid → the primary operator surface.
  const activeTab: OuterTab =
    viewParam && OUTER_TABS.includes(viewParam) ? viewParam : 'pipeline';

  // Keep-alive: a tab mounts on first visit and stays mounted (hidden when inactive) so
  // switching never unmounts the board mid-drag-commit or loses filter state.
  const [mounted, setMounted] = useState<Record<OuterTab, boolean>>(() => ({
    pipeline: false,
    clients: false,
    signups: false,
    analytics: false,
    users: false,
    [activeTab]: true,
  }));
  // The active tab is always mounted — covers back/forward onto a tab never clicked.
  const isMounted = (key: OuterTab) => mounted[key] || key === activeTab;

  // ...and record it, so it STAYS mounted after navigating away again. Without this,
  // only setActiveTab (a click) ever writes `mounted`: a browser back/forward onto a
  // tab mounts it for that render and then throws its state away on the next switch,
  // which is exactly the unmount keep-alive exists to prevent.
  useEffect(() => {
    setMounted((m) => (m[activeTab] ? m : { ...m, [activeTab]: true }));
  }, [activeTab]);

  const setActiveTab = (key: OuterTab) => {
    setMounted((m) => (m[key] ? m : { ...m, [key]: true }));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('view', key);
        return next;
      },
      { replace: true }
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }

  if (!user?.isAdmin) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-error-600" />
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 mb-2">Access Denied</h1>
          <p className="text-neutral-600">
            You don&apos;t have permission to access this page. Only administrators can view this area.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-neutral-50">
      {/* Section switcher — the page chrome itself comes from AppLayout. */}
      <div className="sticky top-0 z-20 bg-white/90 backdrop-blur-sm border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-3">
          <div className="hidden sm:flex items-center gap-2.5 flex-shrink-0">
            <span className="w-2 h-2 rounded-full bg-primary-600" aria-hidden />
            <h1 className="text-[15px] font-semibold tracking-tight text-neutral-900">
              Command Center
            </h1>
          </div>
          <nav
            // min-w-0 lets this flex child actually shrink so overflow-x-auto can
            // engage — without it the pills force the h-14 row wider than the phone.
            className="flex min-w-0 items-center gap-1 bg-neutral-100 rounded-full p-1 overflow-x-auto"
            aria-label="Admin section"
          >
            {TABS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                aria-current={activeTab === key ? 'page' : undefined}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-opacity-10 ${
                  activeTab === key
                    ? 'bg-white text-primary-700 shadow-card'
                    : 'text-neutral-500 hover:text-neutral-800'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab content — keep-alive: mounted tabs stay mounted, hidden when inactive. */}
      <div className={activeTab === 'pipeline' ? '' : 'hidden'}>
        {isMounted('pipeline') && <PipelineBoard />}
      </div>
      <div className={activeTab === 'clients' ? '' : 'hidden'}>
        {isMounted('clients') && <ClientsHub />}
      </div>
      <div className={activeTab === 'signups' ? '' : 'hidden'}>
        {isMounted('signups') && <SignupsTab />}
      </div>
      <div className={activeTab === 'analytics' ? '' : 'hidden'}>
        {isMounted('analytics') && <AnalyticsContent />}
      </div>
      <div className={activeTab === 'users' ? '' : 'hidden'}>
        {isMounted('users') && <UsersTabs />}
      </div>
    </div>
  );
};

export default AnalyticsTabs;
